const KONFIGEAR_BASE = 'https://app.konfigear.com/mtsolar/';

const MODULE_WIDTH_GAP_IN = 0.5;
const MODULE_LENGTH_GAP_IN = 1;

export const parseDesignIntake = (design) => {
    const raw = design?.intake;
    if (raw == null) return null;
    if (typeof raw === 'string') {
        try {
            return JSON.parse(raw);
        } catch {
            return null;
        }
    }
    if (typeof raw === 'object') return raw;
    return null;
};

const num = (value, fallback) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
};

const normalizeOverride = (value, fallback = 'auto') => {
    if (value == null || value === '') return fallback;
    return String(value).toLowerCase() === 'auto' ? 'auto' : value;
};

const computeArrayGeometry = (intake) => {
    const rows = num(intake.number_rows, 4);
    const cols = num(intake.number_columns, 3);
    const moduleWidthIn = num(intake.module_width, 40);
    const moduleLengthIn = num(intake.module_length, 65);

    const arrayWidthIn = cols * moduleLengthIn + Math.max(0, cols - 1) * MODULE_LENGTH_GAP_IN;
    const arrayHeightIn = rows * moduleWidthIn + Math.max(0, rows - 1) * MODULE_WIDTH_GAP_IN;
    const arrayWidthFt = arrayWidthIn / 12;
    const arrayHeightFt = arrayHeightIn / 12;

    return {
        array_width: arrayWidthFt,
        array_height: arrayHeightFt,
        module_area: arrayWidthFt * arrayHeightFt,
        rail_length: arrayWidthIn,
        total_frame_length: arrayHeightFt,
        module_width: moduleWidthIn,
        module_length: moduleLengthIn,
        number_rows: rows,
        number_columns: cols,
        module_width_gap: MODULE_WIDTH_GAP_IN,
        module_length_gap: MODULE_LENGTH_GAP_IN,
    };
};

const buildParamsFromIntake = (design, intake, user) => {
    const geometry = computeArrayGeometry(intake);
    const groundClearance = num(intake.ground_clearance, 5);
    const tiltAngle = num(intake.tilt_angle, 10);
    const frameDuty = intake.frame_duty_override ?? 'auto';
    const poleOverride = intake.pole_spacing_override ?? intake.pole_override ?? 'auto';
    const projectedPanelHeight = 3.26;

    return {
        quote_id: design.design_name ?? '',
        user_id: user?.id ?? '',
        user_email: user?.email ?? '',
        sku_code: design.unique_id ?? '',
        duty_size: normalizeOverride(frameDuty),
        pole_size: normalizeOverride(poleOverride),
        product_type: 'Beam',
        number_of_poles: 1,
        pole_spacing: 0,
        pole_height: 6.63,
        wing_span: Math.round(geometry.array_width * 12) || 84,
        max_foundation_depth: 5.75,
        tilt_angle: tiltAngle,
        cantilever: 258,
        ground_clearance: groundClearance,
        no_poles: 1,
        passed_overhang_check: true,
        ground_to_top: groundClearance + projectedPanelHeight,
        projected_panel_height: projectedPanelHeight,
        wind_speed_override: 'null',
        snow_load_override: 'null',
        direct_snow_load: false,
        add_angle_brace: false,
        designer_name: user?.name ?? user?.full_name ?? '',
        designer_email: user?.email ?? '',
        designer_phone: user?.phone ?? '',
        project_id: design.project_site_id ?? '',
        module_info: '',
        exposure_category: intake.exposure_category ?? 'C',
        frame_duty_override: frameDuty,
        pole_override: poleOverride,
        risk_category: 'I',
        soil_type: 'sand',
        customer_foundation_override: '48_Square',
        foundation_type: 'Square',
        foundation_size: 48,
        check_rails: false,
        total_price: design.total_design_price ?? '',
        total_weight: design.total_design_weight ?? '',
        total_pallets: 2,
        pdf: design.pdf_link ?? design.sky_civ_details?.pdf?.pdf ?? '',
        id: design.id ?? '',
        ...geometry,
    };
};

const buildParamsFromSkyCiv = (design, skyCiv, user) => {
    const { config, frame_data, file_data } = skyCiv;

    return {
        quote_id: design.design_name ?? '',
        user_id: user?.id ?? '',
        user_email: user?.email ?? '',
        sku_code: design.unique_id ?? '',
        duty_size: config.duty_size,
        pole_size: config.pole_size,
        product_type: config.product_type,
        number_of_poles: config.number_of_poles,
        pole_spacing: config.pole_spacing,
        pole_height: config.pole_height,
        wing_span: config.wing_span,
        max_foundation_depth: config.max_foundation_depth,
        tilt_angle: frame_data.tilt_angle,
        cantilever: frame_data.cantilever,
        ground_clearance: frame_data.ground_clearance,
        no_poles: frame_data.no_poles,
        module_area: frame_data.module_area,
        array_width: frame_data.array_width,
        array_height: frame_data.array_height,
        rail_length: frame_data.rail_length,
        module_length: frame_data.module_length,
        passed_overhang_check: frame_data.passed_overhang_check,
        module_width_gap: frame_data.module_width_gap,
        module_length_gap: frame_data.module_length_gap,
        total_frame_length: frame_data.total_frame_length,
        ground_to_top: frame_data.ground_to_top,
        projected_panel_height: frame_data.projected_panel_height,
        wind_speed_override: file_data.wind_speed_override,
        snow_load_override: file_data.snow_load_override,
        direct_snow_load: file_data.direct_snow_load,
        add_angle_brace: file_data.add_angle_brace,
        designer_name: file_data.designer_name,
        designer_email: file_data.designer_email,
        designer_phone: file_data.designer_phone,
        project_id: design.project_site_id ?? '',
        module_info: file_data.module_info,
        module_width: file_data.module_width,
        number_rows: file_data.number_rows,
        number_columns: file_data.number_columns,
        risk_category: file_data.risk_category,
        exposure_category: file_data.exposure_category,
        frame_duty_override: file_data.frame_duty_override,
        pole_override: file_data.pole_override,
        soil_type: file_data.soil_type,
        customer_foundation_override: file_data.customer_foundation_override,
        foundation_type: file_data.foundation_type,
        foundation_size: file_data.foundation_size,
        check_rails: file_data.check_rails,
        total_price: design.total_design_price ?? '',
        total_weight: design.total_design_weight ?? '',
        total_pallets: 2,
        pdf: skyCiv.pdf?.pdf ?? design.pdf_link ?? '',
        id: design.id ?? '',
    };
};

export const getDesignDisplaySpecs = (design) => {
    const intake = parseDesignIntake(design);
    const frame = design?.sky_civ_details?.frame_data;
    const file = design?.sky_civ_details?.file_data;
    const config = design?.sky_civ_details?.config;

    return {
        ground_clearance: frame?.ground_clearance ?? intake?.ground_clearance,
        tilt_angle: frame?.tilt_angle ?? intake?.tilt_angle,
        number_rows: file?.number_rows ?? intake?.number_rows,
        number_columns: file?.number_columns ?? intake?.number_columns,
        module_width: file?.module_width ?? intake?.module_width,
        module_length: file?.module_length ?? intake?.module_length,
        foundation_type: file?.foundation_type,
        foundation_size: file?.foundation_size,
        max_foundation_depth: config?.max_foundation_depth,
        exposure_category: file?.exposure_category ?? intake?.exposure_category,
        frame_duty_override: file?.frame_duty_override ?? intake?.frame_duty_override,
        pole_spacing_override: file?.pole_override ?? intake?.pole_spacing_override,
        beam_series:
            intake?.beam_series ??
            intake?.product_type ??
            design?.product_type ??
            design?.beam_series,
    };
};

export const buildKonfigearDesignLink = (design, user) => {
    const skyCiv = design?.sky_civ_details;
    const intake = parseDesignIntake(design);
    const hasSkyCiv = Boolean(skyCiv?.config && skyCiv?.frame_data && skyCiv?.file_data);

    let params;
    if (hasSkyCiv) {
        params = buildParamsFromSkyCiv(design, skyCiv, user);
    } else if (intake) {
        params = buildParamsFromIntake(design, intake, user);
    } else {
        return KONFIGEAR_BASE;
    }

    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
            search.set(key, String(value));
        }
    });
    return `${KONFIGEAR_BASE}?${search.toString()}`;
};
